import React from "react";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import FormConstants from "./DataEntryFieldsConstants";
import { writeFieldToForm, ClassicFieldMeta, classicFieldErrorMessage } from "./formFieldHelpers";
import TextField from "../../../common/formFields/TextField";

/** Classic's InputFieldLength.REQUEST_ID_MAX_LENGTH (DataEntryDetailsRightPane) - that enum is module-private, so the parity value is restated here. */
const REQUEST_ID_MAX_LENGTH = 30;

export interface RequestIdFieldProps {
    /** Current Request ID (read from DATA_ENTRY_FIELDS_FORM). */
    readonly value?: string;
    /**
     * Hides the field entirely - Classic's `transactionDetails.facility?.useRequestID` gate. Request ID
     * is a per-facility opt-in, so facilities that don't use it must not see the row at all.
     */
    readonly visible: boolean;
    readonly dispatch: Function;
    readonly formName?: string;
    /** Extra, non-form side effect on change (classic pane's autosave PATCH). The DATA_ENTRY_FIELDS_FORM write is always done here. */
    readonly onRequestIdChange?: (newValue?: string) => void;
    /** Real redux-form validation state, threaded down by Classic Mode; omitted by Quick Entry. */
    readonly meta?: ClassicFieldMeta;
}

/**
 * Bug fix (AB#35144): Quick Entry had no Request ID field, so on facilities that use Request IDs the
 * specialist had to leave Quick Entry to set one.
 *
 * Mirrors Classic's own Request ID field: label "Request ID", "XXXXXX-XXXX" placeholder, 30-char cap,
 * and gated on the facility's `useRequestID` flag via `visible`. Not required in Classic.
 */
const RequestIdField: React.FC<RequestIdFieldProps> = (props) => {
    if (!props.visible) {
        return null;
    }

    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;

    // TextField's onChange contract is (value: string), not a React change event.
    //
    // AB#37972 (issue 2) - deliberately NOT routed through the shared Quick Entry sanitizer.
    // Classic's own Request ID field (DataEntryDetailsRightPane.tsx ~2097) is a plain
    // ReduxFormTextField with no `type` prop, so it accepts any character; the identifier's
    // shape is chosen by the facility (this field only renders when `facility.useRequestID` is
    // set), not by this application. A digits-and-hyphen restriction was tried here and reverted
    // after review - it silently corrupted a facility-issued id like "REQ-000123" into "-000123"
    // on the next keystroke. Only the 30-char cap (real Classic parity) is enforced, via
    // `maxLength` below. Routed to product on AB#37972 as a data-format question.
    const onChange = (value: string) => {
        writeFieldToForm(props.dispatch, FormConstants.REQUEST_ID, value, formName);
        props.onRequestIdChange?.(value);
    };

    return (
        <TextField
            label="Request ID"
            placeholder="XXXXXX-XXXX"
            maxLength={REQUEST_ID_MAX_LENGTH}
            errMsg={classicFieldErrorMessage(props.meta)}
            onChange={onChange}
            value={props.value ?? ""}
            name={FormConstants.REQUEST_ID}
        />
    );
};

export default RequestIdField;

export const REQUEST_ID_ROW_ID = "requestId";

/**
 * Builds the FieldGroupRow for the Request ID row. Not required in Classic, so an empty value is
 * EmptyNotRequired and never blocks Confirm All. There is no AI extraction for Request ID (it is a
 * facility/operational identifier, not a value read off the request document), so the row is never
 * "AI pre-selected".
 */
export function mapRequestIdToFieldGroupRow(
    badgeNumber: number,
    formValue: string | undefined,
): FieldGroupRow {
    const trimmed = (formValue ?? "").trim();
    const hasValue = trimmed !== "";

    return {
        id: REQUEST_ID_ROW_ID,
        badgeNumber,
        status: hasValue ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.EmptyNotRequired,
        label: "Request ID",
        value: hasValue ? trimmed : null,
        isAiPreSelected: false,
    };
}
